import { useEffect, useState } from "react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { Bot, ChevronDown, Download, ExternalLink, Lock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PurchaseModal, fmtCoins } from "@/components/shop/PurchaseModal";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  ib_review: "Pending IB Approval",
  pending_admin: "Pending Admin Approval",
  ib_rejected: "Rejected by IB",
  admin_rejected: "Rejected by Admin",
  approved: "Approved",
  paused: "Access Paused",
};
const STATUS_STYLE = {
  ib_review: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  pending_admin: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  approved: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  ib_rejected: "text-red-400 border-red-400/30 bg-red-400/10",
  admin_rejected: "text-red-400 border-red-400/30 bg-red-400/10",
  paused: "text-amber-400 border-amber-400/30 bg-amber-400/10",
};

const shortId = (id) => (id || "").slice(-6);

function StatusChip({ status }) {
  return (
    <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATUS_STYLE[status] || "text-[#94A3B8] border-[#222530]"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function EaGuide({ guide, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-[#222530] px-4 py-2.5 hover:border-[#D4AF37]/30 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Installation Guide</span>
        <span className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">
          {open ? "Less" : "More"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`} data-testid={`${testid}-panel`}>
        <div className="overflow-hidden">
          <div className="mt-2 rounded-lg border border-[#222530] bg-[#0B0C10] px-4 py-3 text-xs text-[#94A3B8] leading-relaxed whitespace-pre-line">
            {guide}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MemberEA() {
  const [eas, setEas] = useState(null);
  const [claims, setClaims] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [balanceMinor, setBalanceMinor] = useState(0);
  const [buyTarget, setBuyTarget] = useState(null);

  const load = () => {
    api.get("/member/expert-advisors").then((res) => setEas(res.data)).catch(() => setEas([]));
    api.get("/member/ea-claims").then((res) => setClaims(res.data)).catch(() => setClaims([]));
    api.get("/member/coins").then((res) => setBalanceMinor(res.data.balance_minor || 0)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const claim = async (ea) => {
    setBusy(ea.id);
    try {
      const res = await api.post(`/member/expert-advisors/${ea.id}/claim`);
      toast.success(res.data.message);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy("");
  };

  const available = (eas || []).filter((ea) => !ea.approved);
  const mine = (eas || []).filter((ea) => ea.approved);

  const claimState = (ea) => {
    const status = ea.my_claim?.status;
    if (!status) return "claim";
    if (status === "ib_rejected" || status === "admin_rejected") return "resubmit";
    return "locked";
  };

  return (
    <div className="max-w-6xl mx-auto space-y-12" data-testid="member-ea-page">
      <div>
        <p className="eyebrow-label">Automated Trading</p>
        <h1 className="page-title mt-2">Expert Advisors</h1>
        <p className="page-sub mt-1">Premium Expert Advisors curated by PlusMaker. Every claim is reviewed by your IB and administration.</p>
      </div>

      {mine.length > 0 && (
        <section className="space-y-5" data-testid="my-ea-section">
          <div>
            <p className="eyebrow-label">My Expert Advisors</p>
            <p className="mt-1 text-xs text-[#64748B]">Your approved Expert Advisors with delivery access.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {mine.map((ea) => (
              <div key={ea.id} className="card-lux overflow-hidden" data-testid={`my-ea-card-${shortId(ea.id)}`}>
                {ea.cover_image_url && (
                  <img loading="lazy" src={`${API}${ea.cover_image_url}`} alt={ea.name} className="h-36 w-full object-cover" />
                )}
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-white font-semibold">{ea.name}</p>
                    <div className="flex items-center gap-1.5">
                      {ea.coin_owned && (
                        <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2 py-0.5" data-testid={`my-ea-owned-${shortId(ea.id)}`}>Owned · Permanent</span>
                      )}
                      <StatusChip status={ea.access_paused ? "paused" : "approved"} />
                    </div>
                  </div>
                  {ea.access_paused && (
                    <p className="text-[11px] text-amber-400 border border-amber-400/25 bg-amber-400/5 rounded-lg px-3.5 py-2.5" data-testid={`my-ea-paused-${shortId(ea.id)}`}>
                      This legacy subscription-linked access is paused — contact support to resume this Expert Advisor.
                    </p>
                  )}
                  {ea.delivery?.files?.length > 0 && (
                    <div className="space-y-2" data-testid={`my-ea-files-${shortId(ea.id)}`}>
                      <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Download Files</p>
                      {ea.delivery.files.map((f) => (
                        <a
                          key={f.index}
                          href={`${API}/api/member/expert-advisors/${ea.id}/download/${f.index}`}
                          className="flex items-center gap-2.5 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-2.5 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                          data-testid={`my-ea-download-${shortId(ea.id)}-${f.index}`}
                        >
                          <Download className="h-3.5 w-3.5" /> {f.name}
                        </a>
                      ))}
                    </div>
                  )}
                  {ea.delivery?.external_link && (
                    <a href={ea.delivery.external_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-4 transition-colors" data-testid={`my-ea-link-${shortId(ea.id)}`}>
                      <ExternalLink className="h-3.5 w-3.5" /> External Access Link
                    </a>
                  )}
                  {ea.delivery?.installation_guide && (
                    <EaGuide guide={ea.delivery.installation_guide} testid={`my-ea-guide-${shortId(ea.id)}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-5">
        <div>
          <p className="eyebrow-label">Available Expert Advisors</p>
          <p className="mt-1 text-xs text-[#64748B]">Browse the catalog and submit a claim. Eligibility depends on your registered broker.</p>
        </div>

        {eas === null ? (
          <p className="text-[#64748B] text-sm">Loading…</p>
        ) : available.length === 0 ? (
          <div className="card-lux p-10 text-center" data-testid="ea-empty">
            <Bot className="h-8 w-8 text-[#D4AF37]/40 mx-auto mb-3" />
            <p className="text-[#64748B] text-sm">No Expert Advisors available right now. Check back soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {available.map((ea) => (
              <div key={ea.id} className="card-lux overflow-hidden flex flex-col" data-testid={`ea-card-${shortId(ea.id)}`}>
                {ea.cover_image_url ? (
                  <img loading="lazy" src={`${API}${ea.cover_image_url}`} alt={ea.name} className="h-36 w-full object-cover" data-testid={`ea-cover-${shortId(ea.id)}`} />
                ) : (
                  <div className="h-36 flex items-center justify-center bg-[#0B0C10]">
                    <Bot className="h-10 w-10 text-[#D4AF37]/40" />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-white font-semibold leading-snug">{ea.name}</p>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {ea.access_type === "coin" ? (
                        <span className="text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2 py-0.5" data-testid={`ea-access-badge-${shortId(ea.id)}`}>Coins</span>
                      ) : (
                        <span className="text-[9px] font-mono tracking-widest uppercase text-emerald-400 border border-emerald-400/30 rounded-full px-2 py-0.5" data-testid={`ea-access-badge-${shortId(ea.id)}`}>Free</span>
                      )}
                      {ea.my_claim && <StatusChip status={ea.my_claim.status} />}
                    </div>
                  </div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed flex-1">{ea.short_description}</p>
                  {ea.coin_price_minor != null && (
                    <p className="text-xs font-mono text-[#D4AF37]" data-testid={`ea-price-${shortId(ea.id)}`}>
                      {fmtCoins(ea.coin_price_minor)} · Permanent ownership
                    </p>
                  )}
                  {ea.my_claim && (ea.my_claim.status === "ib_rejected" || ea.my_claim.status === "admin_rejected") && (
                    <p className="text-[11px] text-red-400/90" data-testid={`ea-reason-${shortId(ea.id)}`}>
                      Reason: {ea.my_claim.status === "admin_rejected" ? ea.my_claim.admin_reason : ea.my_claim.ib_remark}
                    </p>
                  )}
                  {!ea.eligible && (
                    <div className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3.5 py-3" data-testid={`ea-locked-${shortId(ea.id)}`}>
                      <Lock className="h-3.5 w-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <p className="text-[11px] text-[#94A3B8] leading-relaxed">{ea.lock_message}</p>
                    </div>
                  )}
                  {ea.subscription_locked && (
                    <div className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3.5 py-3" data-testid={`ea-sub-locked-${shortId(ea.id)}`}>
                      <Lock className="h-3.5 w-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <p className="text-[11px] text-[#94A3B8] leading-relaxed">{ea.subscription_lock_message}</p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => setDetail(ea)} className="btn-gold-outline flex-1 py-2.5 text-xs" data-testid={`ea-details-${shortId(ea.id)}`}>
                      View Details
                    </button>
                    {ea.coin_price_minor != null && !ea.approved && (
                      <button
                        onClick={() => setBuyTarget({ id: ea.id, name: ea.name, product_type: "ea", price_minor: ea.coin_price_minor, term_label: "Permanent ownership — no expiry" })}
                        className="btn-gold flex-1 py-2.5 text-xs"
                        data-testid={`ea-buy-${shortId(ea.id)}`}
                      >
                        Buy — {fmtCoins(ea.coin_price_minor)}
                      </button>
                    )}
                    {!ea.coin_price_minor && ea.eligible && claimState(ea) !== "locked" && (
                      <button onClick={() => claim(ea)} disabled={busy === ea.id} className="btn-gold flex-1 py-2.5 text-xs disabled:opacity-50" data-testid={`ea-claim-${shortId(ea.id)}`}>
                        {busy === ea.id ? "Submitting…" : claimState(ea) === "resubmit" ? "Resubmit Claim" : "Claim EA"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4" data-testid="ea-history-section">
        <div>
          <p className="eyebrow-label">Claim History</p>
          <p className="mt-1 text-xs text-[#64748B]">Every claim you have submitted, with its full review trail.</p>
        </div>
        {claims.length === 0 ? (
          <p className="text-[#64748B] text-sm" data-testid="ea-history-empty">No claims yet.</p>
        ) : (
          <div className="card-lux divide-y divide-[#222530]">
            {claims.map((cl) => (
              <div key={cl.id} className="flex items-center gap-4 px-5 py-4" data-testid={`ea-history-row-${shortId(cl.id)}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">
                    {cl.ea_name}
                    {cl.subscription_plan && (
                      <span className="ml-2 text-[9px] font-mono tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5">{cl.subscription_plan}</span>
                    )}
                  </p>
                  <p className="text-[10px] text-[#64748B] font-mono mt-0.5">
                    {cl.created_at ? fmtMYT(cl.created_at) : ""}
                  </p>
                </div>
                {(cl.status === "admin_rejected" ? cl.admin_reason : cl.ib_remark) && (
                  <p className="text-[11px] text-[#94A3B8] max-w-[240px] truncate" title={cl.status === "admin_rejected" ? cl.admin_reason : cl.ib_remark}>
                    {cl.status === "admin_rejected" ? cl.admin_reason : cl.ib_remark}
                  </p>
                )}
                <StatusChip status={cl.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      {buyTarget && (
        <PurchaseModal
          product={buyTarget}
          balanceMinor={balanceMinor}
          onClose={() => setBuyTarget(null)}
          onPurchased={load}
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative card-lux w-full max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ea-detail-modal">
            {detail.cover_image_url && <img loading="lazy" src={`${API}${detail.cover_image_url}`} alt={detail.name} className="w-full max-h-56 object-cover" />}
            <div className="p-6 sm:p-8 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-xl font-semibold text-white" data-testid="ea-detail-name">{detail.name}</h2>
                <button onClick={() => setDetail(null)} className="text-[#94A3B8] hover:text-white p-1" data-testid="ea-detail-close" aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {detail.full_description && <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-line">{detail.full_description}</p>}
              {detail.claim_requirements && (
                <div>
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-1.5">Claim Requirements</p>
                  <p className="text-xs text-[#94A3B8] whitespace-pre-line">{detail.claim_requirements}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-1.5">Supported Brokers</p>
                <p className="text-xs text-[#94A3B8]" data-testid="ea-detail-brokers">
                  {detail.allowed_brokers?.length ? detail.allowed_brokers.join(", ") : "Available for all brokers"}
                </p>
              </div>
              {!detail.eligible && (
                <div className="flex items-start gap-2 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-3.5 py-3">
                  <Lock className="h-3.5 w-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
                  <p className="text-[11px] text-[#94A3B8] leading-relaxed">{detail.lock_message}</p>
                </div>
              )}
              {detail.coin_price_minor == null && detail.eligible && claimState(detail) !== "locked" && (
                <button onClick={() => claim(detail)} disabled={busy === detail.id} className="btn-gold w-full py-3 text-xs disabled:opacity-50" data-testid="ea-detail-claim-button">
                  {busy === detail.id ? "Submitting…" : claimState(detail) === "resubmit" ? "Resubmit Claim" : "Claim EA"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
